// src/pages/FinancialsViewPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Col, Row, Spinner, Table, Badge } from "react-bootstrap";
import api from "../api";
import ClientContactLine from "../components/ClientContactLine";

const EXCLUDED_SOURCES = ["snap benefits", "wic"];

// Keep these as fallback defaults if endpoint is missing / returns empty
const DEFAULT_INCOME_SOURCES = [
  "Employment Wage",
  "Social Security",
  "Disability",
  "Unemployment",
  "Family Assistance",
  "Child Support",
  "Pension",
];

const DEFAULT_EXPENSES = [
  "Rent/Mortgage",
  "Food over Snap Benefits",
  "Electricity",
  "Gas",
  "Water",
  "Phone",
  "Car Payment",
  "Insurance",
  "Gasoline/Bus",
  "Medical",
  "Child Care",
  "Smoking",
  "Pets",
  "Child Support",
];

function toNumber(val) {
  if (val === null || val === undefined) return 0;
  const s = String(val).trim();
  if (!s) return 0;
  const cleaned = s.replace(/[$,]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function money(n) {
  const x = Number.isFinite(n) ? n : 0;
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function displayOrDash(v) {
  const s = v == null ? "" : String(v).trim();
  return s ? s : "—";
}
// Show only the last 4 of an account number; never render it in full.
function maskAccount(v) {
  const s = v == null ? "" : String(v).trim();
  if (!s) return "—";
  return `••••${s.slice(-4)}`;
}

function readExtra(row, cols) {
  const e = {};
  for (const c of cols) e[c.key] = row?.[c.key] ?? "";
  return e;
}
function mkIncomeRow(source = "", client = "", spouse = "", extra = {}) {
  return { source, client, spouse, extra };
}
function mkExpenseRow(expense = "", saved = null) {
  return {
    expense,
    amount: saved?.amount ?? "",
    provider: saved?.provider ?? "",
    account_no: saved?.account_no ?? "",
    notes: saved?.notes ?? "",
    date_due: saved?.date_due ?? "",
  };
}

function mergeExpenses(savedExpenses) {
  const byName = new Map(
    (savedExpenses || []).map((r) => [(r.expense ?? "").trim().toLowerCase(), r])
  );
  const rows = DEFAULT_EXPENSES.map((name) => {
    const saved = byName.get(name.toLowerCase());
    byName.delete(name.toLowerCase());
    return mkExpenseRow(name, saved ?? null);
  });
  for (const r of byName.values()) {
    rows.push(mkExpenseRow(r.expense ?? "", r));
  }
  return rows;
}

export default function FinancialsViewPage() {
  const { id } = useParams(); // client id
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [payDays, setPayDays] = useState("");
  const [extraCols, setExtraCols] = useState([]); // [{ key, label }]
  const [incomeRows, setIncomeRows] = useState(DEFAULT_INCOME_SOURCES.map((s) => mkIncomeRow(s)));
  const [snapRow, setSnapRow] = useState({ client: "", spouse: "", extra: {} });
  const [wicRow, setWicRow]   = useState({ client: "", spouse: "", extra: {} });
  const [expenseRows, setExpenseRows] = useState(DEFAULT_EXPENSES.map((e) => mkExpenseRow(e)));

  const extraSum = (extra) => extraCols.reduce((s, c) => s + toNumber(extra?.[c.key]), 0);

  // SNAP Benefits and WIC are intentionally excluded from this total.
  // Every earner column (client, spouse, and any extra columns) is counted.
  const incomeTotals = useMemo(() => {
    const clientTotal = incomeRows.reduce((sum, r) => sum + toNumber(r.client), 0);
    const spouseTotal = incomeRows.reduce((sum, r) => sum + toNumber(r.spouse), 0);
    const extraTotals = {};
    let combinedExtra = 0;
    for (const c of extraCols) {
      const t = incomeRows.reduce((sum, r) => sum + toNumber(r.extra?.[c.key]), 0);
      extraTotals[c.key] = t;
      combinedExtra += t;
    }
    return {
      clientTotal,
      spouseTotal,
      extraTotals,
      combined: clientTotal + spouseTotal + combinedExtra,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeRows, extraCols]);

  const expenseTotal = useMemo(() => {
    return expenseRows.reduce((sum, r) => sum + toNumber(r.amount), 0);
  }, [expenseRows]);

  const netMonthly = useMemo(() => {
    return incomeTotals.combined - expenseTotal;
  }, [incomeTotals.combined, expenseTotal]);

  // ---- Load existing financials (non-blocking) ----
  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const res = await api.get(`/clients/${id}/financials`);
        const data = res?.data || {};
        if (!isMounted) return;

        setPayDays(data.pay_days ?? "");

        const cols = Array.isArray(data.income_columns)
          ? data.income_columns.map((c) => ({ key: String(c.key), label: String(c.label ?? "") }))
          : [];
        setExtraCols(cols);

        if (Array.isArray(data.income) && data.income.length) {
          const snap = data.income.find((r) => (r.source ?? "").toLowerCase() === "snap benefits");
          const wic  = data.income.find((r) => (r.source ?? "").toLowerCase() === "wic");
          setSnapRow({ client: snap?.client ?? "", spouse: snap?.spouse ?? "", extra: readExtra(snap, cols) });
          setWicRow({ client: wic?.client ?? "", spouse: wic?.spouse ?? "", extra: readExtra(wic, cols) });
          setIncomeRows(
            data.income
              .filter((r) => !EXCLUDED_SOURCES.includes((r.source ?? "").toLowerCase()))
              .map((r) => mkIncomeRow(r.source ?? "", r.client ?? "", r.spouse ?? "", readExtra(r, cols)))
          );
        } else {
          setIncomeRows(DEFAULT_INCOME_SOURCES.map((s) => mkIncomeRow(s)));
        }

        setExpenseRows(mergeExpenses(data.expenses));
      } catch (e) {
        // Match your edit behavior: don't hard-fail; show defaults.
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="p-3">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading financials...
      </div>
    );
  }

  return (
    <div className="p-3">
      <Row className="mb-3">
        <Col>
          <h3 className="mb-0">Financials</h3>
          <ClientContactLine clientId={id} />
          <div className="text-muted">Client ID: {id}</div>

          <div className="text-muted mt-1" style={{ fontSize: "0.95rem" }}>
            <span className="me-2">
              <b>Total Income:</b> {money(incomeTotals.combined)} <span style={{ fontSize: "0.85em" }}>(excl. SNAP/WIC)</span>
            </span>
            <span className="me-2">
              <b>Total Expenses:</b> {money(expenseTotal)}
            </span>
            <span>
              <b>Net:</b>{" "}
              <Badge bg={netMonthly >= 0 ? "success" : "danger"}>
                {money(netMonthly)}
              </Badge>
            </span>
          </div>
        </Col>

        <Col className="d-flex justify-content-end align-items-start gap-2">
          <Button variant="outline-secondary" type="button" onClick={() => navigate("/clients")}>
            Back to Clients
          </Button>
          {/* EDIT tab route (your existing edit screen) */}
          <Button variant="primary" type="button" onClick={() => navigate(`/clients/${id}/financials`)}>
            Edit Financials
          </Button>
        </Col>
      </Row>

      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}

      <Card className="mb-3">
        <Card.Header>Pay days</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} lg={4}>
              <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                Pay days
              </div>
              <div>{displayOrDash(payDays)}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Income</Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th style={{ minWidth: 180 }}>Income Source</th>
                <th style={{ minWidth: 110 }}>Client</th>
                <th style={{ minWidth: 110 }}>Spouse</th>
                {extraCols.map((c) => (
                  <th key={c.key} style={{ minWidth: 120 }}>{displayOrDash(c.label)}</th>
                ))}
                <th style={{ minWidth: 110 }}>Combined</th>
              </tr>
            </thead>
            <tbody>
              {incomeRows.map((r, idx) => {
                const c = toNumber(r.client);
                const s = toNumber(r.spouse);
                const combined = c + s + extraSum(r.extra);
                return (
                  <tr key={`${r.source}-${idx}`}>
                    <td>{displayOrDash(r.source)}</td>
                    <td>{c ? money(c) : "—"}</td>
                    <td>{s ? money(s) : "—"}</td>
                    {extraCols.map((col) => {
                      const v = toNumber(r.extra?.[col.key]);
                      return <td key={col.key}>{v ? money(v) : "—"}</td>;
                    })}
                    <td>{combined ? money(combined) : "—"}</td>
                  </tr>
                );
              })}

              {[{ label: "SNAP Benefits", row: snapRow }, { label: "WIC", row: wicRow }].map(({ label, row }) => {
                const c = toNumber(row.client);
                const s = toNumber(row.spouse);
                const combined = c + s + extraSum(row.extra);
                return (
                  <tr key={label} className="table-light">
                    <td className="fst-italic text-muted">{label}</td>
                    <td>{c ? money(c) : "—"}</td>
                    <td>{s ? money(s) : "—"}</td>
                    {extraCols.map((col) => {
                      const v = toNumber(row.extra?.[col.key]);
                      return <td key={col.key}>{v ? money(v) : "—"}</td>;
                    })}
                    <td>{combined ? money(combined) : "—"}</td>
                  </tr>
                );
              })}

              <tr>
                <td className="fw-bold">Total Income <span className="fw-normal text-muted" style={{ fontSize: "0.8em" }}>(excl. SNAP/WIC)</span></td>
                <td className="fw-bold">{money(incomeTotals.clientTotal)}</td>
                <td className="fw-bold">{money(incomeTotals.spouseTotal)}</td>
                {extraCols.map((col) => (
                  <td key={col.key} className="fw-bold">{money(incomeTotals.extraTotals[col.key] || 0)}</td>
                ))}
                <td className="fw-bold">{money(incomeTotals.combined)}</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Expenses</Card.Header>

        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th style={{ minWidth: 220 }}>Expenses</th>
                <th style={{ width: 140 }}>Amount</th>
                <th style={{ minWidth: 200 }}>Provider</th>
                <th style={{ width: 140 }}>Account#</th>
                <th style={{ minWidth: 240 }}>Notes</th>
                <th style={{ width: 160 }}>Date Due</th>
              </tr>
            </thead>
            <tbody>
              {expenseRows.map((r, idx) => (
                <tr key={`${r.expense}-${idx}`}>
                  <td>{displayOrDash(r.expense)}</td>
                  <td>{toNumber(r.amount) ? money(toNumber(r.amount)) : "—"}</td>
                  <td>{displayOrDash(r.provider)}</td>
                  <td>{maskAccount(r.account_no)}</td>
                  <td>{displayOrDash(r.notes)}</td>
                  <td>{displayOrDash(r.date_due)}</td>
                </tr>
              ))}

              <tr>
                <td className="fw-bold">Total Expenses</td>
                <td className="fw-bold">{money(expenseTotal)}</td>
                <td colSpan={4}></td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
}
